// src/finance/components/RevenueDonut.tsx
// Donut à deux anneaux : les familles à l'intérieur, les huit postes à l'extérieur.
// Huit teintes distinctes ne passent pas le validateur dataviz (ΔE < 15 en vision
// normale) : l'identité d'un poste vient donc de sa famille, de la légende et du
// survol — jamais de sa seule couleur.
import { useState } from 'react'
import type { DonutData, DonutGroup } from '../donut'
import { arcPath } from '../donut'
import { round2 } from '../engine/tax'
import { fmtEur } from '../format'

const SIZE = 220
const C = SIZE / 2
/** Bande fine hors anneau : la portion des postes que le CA ne couvre pas. */
const OVERDRAFT = { r1: 109, r0: 104 }
const OUTER = { r1: 100, r0: 72 }
const INNER = { r1: 68, r0: 46 }
/** Retrait angulaire donnant un intervalle d'environ 2px au rayon extérieur. */
const GAP = 2 / OUTER.r1

const GROUP_COLOR: Record<DonutGroup, string> = {
  expenses: 'var(--viz-expenses)',
  levies: 'var(--viz-levies)',
  living: 'var(--viz-living)',
  remaining: 'var(--viz-net)',
}

const GROUP_LABEL: Record<DonutGroup, string> = {
  expenses: 'Dépenses société',
  levies: 'Prélèvements',
  living: 'Dépenses perso',
  remaining: 'Ce qui te reste',
}

/** Nuance d'un poste dans sa famille : la teinte dit le groupe, la clarté le rang. */
function sliceTint(group: DonutGroup, rankInGroup: number, groupSize: number): string {
  if (groupSize <= 1) return GROUP_COLOR[group]
  const step = rankInGroup / (groupSize - 1)
  return `color-mix(in oklab, ${GROUP_COLOR[group]} ${Math.round(100 - step * 42)}%, #f2e4cf)`
}

const pct = (share: number) => `${Math.round(share * 100)} %`
const pct1 = (share: number) => `${(share * 100).toFixed(1).replace('.', ',')} %`

/** Ce que le pointeur (ou le focus) désigne : un poste, ou une famille entière. */
type Active = { kind: 'slice'; key: string } | { kind: 'group'; group: DonutGroup } | null

export function RevenueDonut({ data }: { data: DonutData }) {
  const [active, setActive] = useState<Active>(null)
  const { slices, total, spent, overdraft, remaining } = data
  /**
   * Sur un CA nul, aucun pourcentage n'est calculable : `share` vaut 0 partout
   * alors que l'arc occupe TOUT l'anneau (la géométrie est normalisée sur la somme
   * des postes). La légende affichait « Dépenses perso — 2 000 € — 0 % » sous un
   * arc plein : le chiffre et l'image se contredisaient. On n'annonce donc rien.
   */
  const pctOf = (share: number): string => (total > 0 ? pct(share) : '—')
  const pct1Of = (share: number): string => (total > 0 ? pct1(share) : '—')

  // À perte, aucune vue proportionnelle n'est défendable : l'anneau afficherait
  // une part unique « 100 % » valant plus que le CA. On dit le déficit réel.
  if (data.isLoss) {
    return (
      <div
        data-testid="donut-loss"
        style={{
          border: '1px solid var(--arancio)',
          borderRadius: '8px',
          padding: '0.75rem 0.875rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.25rem',
        }}
      >
        <span style={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: 'var(--mono)', color: 'var(--arancio)' }}>
          {fmtEur(-data.deficit)}
          <span style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 500 }}> /mois</span>
        </span>
        <span style={{ fontSize: '0.75rem', color: 'var(--fg)' }}>
          Déficit — le CA ({fmtEur(total)}) ne couvre pas les dépenses société.
        </span>
        <span style={{ fontSize: '0.6875rem', color: 'var(--dim)' }}>
          Rien à répartir : aucun revenu personnel n’est possible dans ce scénario.
        </span>
      </div>
    )
  }

  // Un CA nul SANS dépense saisie n'a rien à montrer ; un découvert, si.
  if (slices.length === 0 && overdraft <= 0) return null

  // Géométrie : l'anneau se referme sur la somme des postes. En découvert cette
  // somme dépasse le CA, et la bande extérieure marque précisément l'excédent —
  // les POURCENTAGES, eux, restent rapportés au CA réel (donut.ts).
  const angleOf = (amount: number) => (spent > 0 ? amount / spent : 0)
  const coveredAngle = spent > 0 ? Math.min(1, total / spent) : 1

  const activeSlice = active?.kind === 'slice' ? (slices.find((s) => s.key === active.key) ?? null) : null
  // Totaux par famille — agrégés sur toutes les parts, pas sur l'arc contigu :
  // un poste omis (montant nul) ne doit pas scinder le total de sa famille.
  const groupTotal = (group: DonutGroup) => {
    const peers = slices.filter((s) => s.group === group)
    return {
      amount: peers.reduce((t, s) => t + s.amount, 0),
      share: peers.reduce((t, s) => t + s.share, 0),
      count: peers.length,
    }
  }
  const activeGroupKey =
    active?.kind === 'group' ? active.group : activeSlice ? activeSlice.group : null

  // Angles cumulés des postes (anneau extérieur).
  let cursor = 0
  const arcs = slices.map((slice) => {
    const start = cursor
    cursor += angleOf(slice.amount) * Math.PI * 2
    return { slice, start, end: cursor }
  })

  // Familles contiguës (anneau intérieur) : les postes d'un même groupe se suivent.
  const groups: { group: DonutGroup; start: number; end: number }[] = []
  for (const arc of arcs) {
    const last = groups[groups.length - 1]
    if (last && last.group === arc.slice.group) last.end = arc.end
    else groups.push({ group: arc.slice.group, start: arc.start, end: arc.end })
  }

  const rankInGroup = (key: string, group: DonutGroup) => {
    const peers = slices.filter((s) => s.group === group)
    return { rank: peers.findIndex((s) => s.key === key), size: peers.length }
  }

  const description = slices.map((s) => `${s.label} ${pctOf(s.share)}`).join(', ')

  return (
    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: 0 }}>
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          role="img"
          aria-label={`Répartition du CA : ${description}`}
        >
          {groups.map((g) => {
            const t = groupTotal(g.group)
            const isActive = activeGroupKey === g.group
            return (
              <path
                key={`${g.group}-${g.start.toFixed(4)}`}
                data-testid="donut-group"
                tabIndex={0}
                role="button"
                aria-label={`${GROUP_LABEL[g.group]}, ${t.count} poste${t.count > 1 ? 's' : ''} : ${fmtEur(t.amount)}, ${pct1Of(t.share)}`}
                d={arcPath(C, C, INNER.r1, INNER.r0, g.start + GAP, Math.max(g.start + GAP, g.end - GAP))}
                fill={GROUP_COLOR[g.group]}
                opacity={activeGroupKey && !isActive ? 0.3 : 0.9}
                style={{
                  cursor: 'pointer',
                  outline: 'none',
                  filter: active?.kind === 'group' && isActive ? 'brightness(1.15)' : undefined,
                }}
                onMouseEnter={() => setActive({ kind: 'group', group: g.group })}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive({ kind: 'group', group: g.group })}
                onBlur={() => setActive(null)}
              />
            )
          })}
          {arcs.map(({ slice, start, end }) => {
            const { rank, size } = rankInGroup(slice.key, slice.group)
            const isActive = activeSlice?.key === slice.key
            // Survoler une famille met en avant tous ses postes ; survoler un poste
            // n'éclaire que lui (mais laisse sa famille lisible à l'intérieur).
            const isDimmed =
              active !== null &&
              !isActive &&
              !(active.kind === 'group' && active.group === slice.group)
            return (
              <path
                key={slice.key}
                data-testid="donut-slice"
                tabIndex={0}
                role="button"
                aria-label={`${slice.label} : ${fmtEur(slice.amount)}, ${pct1Of(slice.share)}`}
                d={arcPath(C, C, OUTER.r1, OUTER.r0, start + GAP, Math.max(start + GAP, end - GAP))}
                fill={sliceTint(slice.group, rank, size)}
                opacity={isDimmed ? 0.45 : 1}
                style={{ cursor: 'pointer', outline: 'none', filter: isActive ? 'brightness(1.15)' : undefined }}
                onMouseEnter={() => setActive({ kind: 'slice', key: slice.key })}
                onMouseLeave={() => setActive(null)}
                onFocus={() => setActive({ kind: 'slice', key: slice.key })}
                onBlur={() => setActive(null)}
              />
            )
          })}
          {/* Bande extérieure : la fraction des postes que le CA ne couvre PAS.
              Elle commence là où le CA est épuisé et court jusqu'à la fermeture
              de l'anneau — l'œil voit donc de combien la dépense déborde. */}
          {overdraft > 0 && (
            <path
              data-testid="donut-overdraft-arc"
              aria-label={`À découvert : ${fmtEur(overdraft)}`}
              d={arcPath(C, C, OVERDRAFT.r1, OVERDRAFT.r0, coveredAngle * Math.PI * 2, Math.PI * 2)}
              fill="var(--arancio)"
            />
          )}
        </svg>

        {/* Zone de lecture fixe : elle affiche le total au repos, donc aucun saut
            de hauteur quand le pointeur entre et sort du graphique. */}
        <div
          data-testid="donut-readout"
          style={{
            minHeight: '76px',
            border: '1px solid var(--line)',
            borderRadius: '8px',
            padding: '0.5rem 0.625rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.125rem',
            width: `${SIZE}px`,
          }}
        >
          {(() => {
            if (active === null) {
              return (
                <>
                  <span style={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: 'var(--mono)', color: 'var(--fg)' }}>
                    {fmtEur(total)}
                  </span>
                  <span style={{ fontSize: '0.6875rem', color: 'var(--dim)' }}>
                    CA réparti — Survole une part pour le détail
                  </span>
                </>
              )
            }
            // Anneau intérieur : le total d'une famille et le nombre de postes qu'elle réunit.
            if (active.kind === 'group') {
              const t = groupTotal(active.group)
              return (
                <>
                  <span style={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: 'var(--mono)', color: 'var(--fg)' }}>
                    {fmtEur(t.amount)}
                    <span style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 500 }}> /mois</span>
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--fg)' }}>
                    {GROUP_LABEL[active.group]} — {t.count} poste{t.count > 1 ? 's' : ''}
                  </span>
                  <span style={{ fontSize: '0.6875rem', color: 'var(--muted)' }}>
                    {pct1Of(t.share)} du CA · {fmtEur(round2(t.amount * 12))} /an
                  </span>
                </>
              )
            }
            if (activeSlice === null) return null
            return (
              <>
                <span style={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: 'var(--mono)', color: 'var(--fg)' }}>
                  {fmtEur(activeSlice.amount)}
                  <span style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 500 }}> /mois</span>
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--fg)' }}>{activeSlice.label}</span>
                <span style={{ fontSize: '0.6875rem', color: 'var(--muted)' }}>
                  {pct1Of(activeSlice.share)} du CA · {fmtEur(round2(activeSlice.amount * 12))} /an
                </span>
                <span style={{ fontSize: '0.6875rem', color: 'var(--dim)' }}>{GROUP_LABEL[activeSlice.group]}</span>
              </>
            )
          })()}
        </div>

        {/* Le reste après dépenses perso est négatif : l'arc correspondant ne peut
            pas être tracé, mais le montant doit se lire en clair — sans quoi le
            donut donnerait à croire que tout tient dans le CA. */}
        {overdraft > 0 && (
          <p
            data-testid="donut-overdraft"
            style={{
              margin: 0,
              width: `${SIZE}px`,
              fontSize: '0.6875rem',
              lineHeight: 1.45,
              color: 'var(--arancio)',
            }}
          >
            À découvert :{' '}
            <b style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'tabular-nums' }}>{fmtEur(overdraft)}</b> /mois —
            les postes dépassent le CA ({fmtEur(total)}).
          </p>
        )}

        {/* La grandeur que l'utilisateur vient chercher — ce qui lui reste — était
            purement escamotée quand elle passait sous zéro : absente de la légende,
            absente de l'aria-label, et remplacée par un découvert imputé au CA
            alors que la cause est le train de vie. On la nomme et on la chiffre. */}
        {remaining < 0 && (
          <p
            data-testid="donut-remaining-negative"
            style={{
              margin: 0,
              width: `${SIZE}px`,
              fontSize: '0.6875rem',
              lineHeight: 1.45,
              color: 'var(--arancio)',
            }}
          >
            Ce qui te reste :{' '}
            <b style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'tabular-nums' }}>{fmtEur(remaining)}</b> /mois —
            tes dépenses perso dépassent ton net. Le poste n’a pas d’arc : un arc ne peut pas être négatif.
          </p>
        )}
      </div>

      <ul
        data-testid="donut-legend"
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.2rem',
          flex: 1,
          minWidth: '190px',
        }}
      >
        {slices.map((slice) => {
          const { rank, size } = rankInGroup(slice.key, slice.group)
          // Une famille survolée éclaire toutes ses lignes, pas seulement la sienne.
          const isActive =
            activeSlice?.key === slice.key ||
            (active?.kind === 'group' && active.group === slice.group)
          return (
            <li
              key={slice.key}
              onMouseEnter={() => setActive({ kind: 'slice', key: slice.key })}
              onMouseLeave={() => setActive(null)}
              style={{
                display: 'grid',
                gridTemplateColumns: '10px 1fr auto auto',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.75rem',
                padding: '0.15rem 0.25rem',
                borderRadius: '4px',
                background: isActive ? 'var(--elevated)' : 'transparent',
                cursor: 'default',
              }}
            >
              <i
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '2px',
                  background: sliceTint(slice.group, rank, size),
                }}
              />
              <span style={{ color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {slice.label}
              </span>
              <span style={{ fontFamily: 'var(--mono)', fontVariantNumeric: 'tabular-nums', color: 'var(--fg)' }}>
                {fmtEur(slice.amount)}
              </span>
              <span
                style={{
                  fontFamily: 'var(--mono)',
                  fontVariantNumeric: 'tabular-nums',
                  color: 'var(--dim)',
                  minWidth: '38px',
                  textAlign: 'right',
                }}
              >
                {pctOf(slice.share)}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
